import React, { useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CustomTable, { renderBooleanCell } from '../misc/CustomTable';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectAgreementsData } from '../../store/general/general-slice-selectors';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { dateToStringFormat } from '@common/utils/date';
import ListAgreement from '../../models/general/agreements/list-agreement';
import { AgreementType } from '../../models/enums/agreements';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getAgreementById } from '../../store/general/general-actions';

const renderDateCreated = (params: GridRenderCellParams) => {
  return dateToStringFormat(params.value);
};

const AgreementAcceptances: React.FC<{ agreement: ListAgreement }> = ({ agreement }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));

  useEffect(() => {
    if (agreementsData.rest.getAgreementStatus === HttpFetchStatus.Idle) {
      dispatch(getAgreementById(agreement.id));
    }
  }, [agreement.id, agreementsData.rest.getAgreementStatus, dispatch]);

  const organizationColumns: GridColDef[] = [
    {
      field: 'organization',
      headerName: t('table-header-organization') as string,
      width: 150,
      valueGetter: (_, row) => {
        return row.organization.name;
      },
    },
    {
      field: 'user',
      headerName: t('table-header-accepted-by-user') as string,
      width: 150,
      valueGetter: (_, row) => {
        return row.user.name;
      },
    },
  ];

  const userColumns: GridColDef[] = [
    {
      field: 'user',
      headerName: t('table-header-name') as string,
      width: 150,
      valueGetter: (_, row) => {
        return row.user.name;
      },
    },
    {
      field: 'organization',
      headerName: t('table-header-organization') as string,
      width: 150,
      valueGetter: (_, row) => {
        return row.user.organization.name;
      },
    },
    {
      field: 'email',
      headerName: t('table-header-email') as string,
      width: 250,
      valueGetter: (_, row) => {
        return row.user.email;
      },
    },
  ];

  const generalColumns: GridColDef[] = [
    {
      field: 'dateCreated',
      headerName: t('table-header-agreement-accepted-date') as string,
      width: 200,
      renderCell: renderDateCreated,
    },
    {
      field: 'accepted',
      headerName: t('table-header-agreement-accepted') as string,
      renderCell: renderBooleanCell,
      width: 150,
    },
  ];

  return (
    <React.Fragment>
      <Stack>
        <Stack direction="row" alignContent="center" justifyContent="space-between" pt={4} pb={2}>
          <Typography variant="h2">{t('title-acceptances')}</Typography>
        </Stack>
        <CustomTable
          getStatus={agreementsData.rest.getAgreementStatus}
          loadingMessage={t('loading-agreement-details')}
          dataLoaded={agreementsData.currentDetails != null}
          failedToLoadMessage={t('error-loading-agreement-details')}
          emptyDataMessage={t('info-no-agreement-acceptances')}
          data={agreementsData.currentDetails?.acceptances ?? []}
          columns={(agreement.agreementType === AgreementType.USER
            ? userColumns
            : organizationColumns
          ).concat(generalColumns)}
        />
      </Stack>
    </React.Fragment>
  );
};

export default AgreementAcceptances;
